// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  CHECKOUT_FEATURE_KEY,
  prePayCheck,
  addPayMethod,
  addServiceProduct,
  changeDeliveryDate,
  updateOrderForStripeLinkPay,
  updateOrderForPay,
  sendStripePaymentLink,
  setExchangeOrderNumber,
  setOrderComment,
  setAssemblyPreferences,
  confirmPayments,
  setTradePartnerId,
  removePayMethod,
  getStripeClientSecret,
  type AddServiceProductPayload,
  type UpdateOrderForStripeLinkPayRequest,
  completePay,
  changeAddressByOrderNumber,
  changeDeliveryOption,
} from '@castlery/modules-checkout-domain';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@castlery/shared-redux-store';
import { selectCurrentOrderNumber, selectOrderShipments, getOrderByOrderNumber } from '@castlery/modules-order-domain';
import { addAddressToUserByUid, getAddressesByUserId } from '@castlery/modules-user-domain';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { selectCheckoutAddress } from '@castlery/modules-composite-services';
import { handlePurchase } from '@castlery/modules-tracking-domain';
import { stripeUtil } from '@castlery/utils';

/**
 * Add service product command
 * @description add disposal service or add carry-up service
 */
export const addServiceProductCommand = createAsyncThunk(
  'addServiceProductCommand',
  async (payload: AddServiceProductPayload & { type: string }, { dispatch, rejectWithValue, getState }) => {
    const rootState = getState() as RootState;
    const { type, ...rest } = payload;
    const targetShipment = selectOrderShipments(rootState)?.find((item) => item.id === rest.shipment_id);
    const selectedServices = targetShipment?.selected_service_products;

    const extraServices = selectedServices?.filter((item) => item.type !== type) || [];
    const finalExtraServices = extraServices.map((item) => {
      return item.type === 'disposal' ? { ...item, size: item.custom_attributes.disposal_item_size } : { ...item };
    });
    const services = [...finalExtraServices, ...rest.services];
    const result = {
      ...rest,
      services,
    } as AddServiceProductPayload;
    const res = await dispatch(addServiceProduct.initiate(result));
    if ('error' in res) {
      return rejectWithValue(res?.error);
    }
    return res.data;
  }
);

/**
 * Change delivery date command
 * @description change delivery date for re-schedule delivery
 */
export const changeDeliveryDateCommand = createAsyncThunk(
  'changeDeliveryDateCommand',
  async (payload: { number: string; shipmentId: number; deliveryDate: string }, { dispatch, rejectWithValue }) => {
    const res = await dispatch(changeDeliveryDate.initiate(payload));
    if ('error' in res) {
      return rejectWithValue(res?.error);
    }
    return res.data;
  }
);

/**
 * Add pay method to order command
 * @description add pay method to order
 */
export const addPayMethodToOrderCommand = createAsyncThunk(
  'addPayMethodToOrderCommand',
  async (
    payload: { orderNumber: string; id: number; name: string; payType: string; comment: string; amount: number },
    { dispatch, rejectWithValue }
  ) => {
    //Step1 :prepay check
    const checkRes = await dispatch(prePayCheck.initiate({ number: payload.orderNumber }));
    if ('error' in checkRes) {
      return rejectWithValue(checkRes?.error);
    }
    const params = {
      payments_attributes: {
        payment_method_id: payload.id,
        source_attributes: {
          payment_type: payload.payType,
          remarks: payload.comment,
        },
        amount: payload.amount,
      },
    };
    // Step2: add pay method
    const addRes = await dispatch(addPayMethod.initiate({ ...params, number: payload.orderNumber }));
    if ('error' in addRes) {
      return rejectWithValue(addRes?.error);
    }
    return addRes?.data;
  }
);

export const addStripePayMethodCommand = createAsyncThunk(
  'addStripePayMethodCommand',
  async (
    payload: {
      orderNumber: string;
      id: number;
      name: string;
      payType: string;
      comment: string;
      amount: number;
      stripeProcessingHandler: (status: 'pending' | 'success' | 'failed') => void;
    },

    { dispatch, rejectWithValue }
  ) => {
    //Prepare: check reader status
    const readerConnected = stripeUtil.checkConnectStatus();
    if (!readerConnected) {
      return rejectWithValue('Please connect reader first!');
    }
    //Step1: prepay check
    const checkRes = await dispatch(prePayCheck.initiate({ number: payload.orderNumber }));
    if ('error' in checkRes) {
      return rejectWithValue(checkRes?.error);
    }
    // Step2: get stripe client secret
    const params = {
      payments_attributes: {
        payment_method_id: payload.id,
        source_attributes: {
          payment_type: payload.payType,
          remarks: payload.comment,
        },
        amount: payload.amount,
      },
    };
    const res = await dispatch(
      getStripeClientSecret.initiate({ amount: params.payments_attributes.amount, number: payload.orderNumber })
    );
    if ('error' in res) {
      return rejectWithValue(res?.error);
    }
    // Step3: exec stripe sdk method: collect payment method
    const { client_secret } = res.data;
    const terminal = (window as any)?.terminal;
    if (!terminal) {
      return rejectWithValue('window.terminal not found');
    }
    if (!client_secret) {
      return rejectWithValue('client_secret not found');
    }
    payload.stripeProcessingHandler('pending');
    //https://docs.stripe.com/terminal/references/api/js-sdk#get-connection-status
    const { error, paymentIntent } = await terminal.collectPaymentMethod(client_secret);
    if (error) {
      payload.stripeProcessingHandler('failed'); //error.code => "payment_intent_invalid_parameter"
      return rejectWithValue(error.message);
    }
    // Step4: process payment
    if (paymentIntent) {
      const { error: processError, paymentIntent: processPaymentIntent } = await terminal.processPayment(paymentIntent);

      if (processError) {
        payload.stripeProcessingHandler('failed');
        return rejectWithValue(processError.message);
      }
      const cardDetail = processPaymentIntent.charges.data[0].payment_method_details.card_present;
      const externalReference = processPaymentIntent.charges.data[0].id;
      // const numOfPaymentsBefore = getState().order?.order?.payments.data.length;
      const newParams = {
        payments_attributes: {
          ...params.payments_attributes,
          source_attributes: {
            ...params.payments_attributes.source_attributes,
            payment_intent_id: processPaymentIntent.id,
            card_brand: cardDetail.brand,
            card_entry_method: cardDetail.read_method,
            expiration_date: `${cardDetail.exp_month}/${cardDetail.exp_year}`,
            masked_pan: `****${cardDetail.last4}`,
            external_reference: externalReference,
          },
        },
      };
      // Step5: add pay method
      const addRes = await dispatch(addPayMethod.initiate({ ...newParams, number: payload.orderNumber }));
      if ('error' in addRes) {
        payload.stripeProcessingHandler('failed');
        return rejectWithValue(addRes?.error);
      }
      payload.stripeProcessingHandler('success');
      return addRes?.data;
    }
    payload.stripeProcessingHandler('failed');
    return rejectWithValue('[addStripePayMethod]paymentIntent not found');
  }
);

/**
 * Stripe Link Pay command
 * @description create stripe link pay command
 */
export const stripeLinkPayCommand = createAsyncThunk(
  'stripeLinkPayCommand',
  async (payload: { number: string }, { dispatch, getState, rejectWithValue }) => {
    if (!payload.number) {
      return rejectWithValue('Invalid order number');
    }
    // Step1 :change order
    const rootState = getState() as RootState;
    const { tradePartnerId, orderComment, exchangeOrderNumber, assemblyPreferences } = rootState[CHECKOUT_FEATURE_KEY];
    const params = {
      number: payload.number,
      special_instructions: orderComment ?? '',
      exchange_order_number: exchangeOrderNumber ?? '',
      assembly_preferences: assemblyPreferences ?? [],
      trade_partner_id: tradePartnerId ?? '',
    } as UpdateOrderForStripeLinkPayRequest;
    const res = await dispatch(updateOrderForStripeLinkPay.initiate(params));
    if ('error' in res) {
      return rejectWithValue(res?.error);
    }
    // Step2: add pay method
    const payRes = await dispatch(sendStripePaymentLink.initiate({ number: payload.number }));
    if ('error' in payRes) {
      // 失败时，刷新order
      await dispatch(getOrderByOrderNumber.initiate(payload.number));
      return rejectWithValue(payRes?.error);
    }
    return payRes.data;
  }
);

export const updateExchangeOrderNumber = createAsyncThunk(
  'updateExchangeOrderNumber',
  async (exchangeOrderNumber: string, { dispatch }) => {
    dispatch(setExchangeOrderNumber(exchangeOrderNumber));
  }
);

export const updateOrderComment = createAsyncThunk('updateOrderComment', async (orderComment: string, { dispatch }) => {
  dispatch(setOrderComment(orderComment));
});

//available_assembly_preferences
export const updateAssemblyPreferences = createAsyncThunk(
  'updateAssemblyPreferences',
  async (assemblyPreferences: string[], { dispatch }) => {
    dispatch(setAssemblyPreferences(assemblyPreferences));
  }
);
export const updateTradePartnerId = createAsyncThunk(
  'updateTradePartnerId',
  async (tradePartnerId: string, { dispatch }) => {
    dispatch(setTradePartnerId(tradePartnerId));
  }
);

/**
 * Confirm pay command
 */
export const confirmPayCommand = createAsyncThunk(
  'confirmPayCommand',
  async (orderNumber: string, { dispatch, getState, rejectWithValue }) => {
    if (!orderNumber || orderNumber === 'undefined') {
      return rejectWithValue('[confirmPayCommand]:order not found');
    }
    const rootState = getState() as RootState;

    // Step1: update order
    const { tradePartnerId, orderComment, exchangeOrderNumber, assemblyPreferences } = rootState[CHECKOUT_FEATURE_KEY];
    const params = {
      number: orderNumber,
      special_instructions: orderComment ?? '',
      exchange_order_number: exchangeOrderNumber ?? '',
      assembly_preferences: assemblyPreferences ?? [],
      trade_partner_id: tradePartnerId ?? '',
    } as UpdateOrderForStripeLinkPayRequest;

    const res = await dispatch(updateOrderForPay.initiate(params));
    if ('error' in res) {
      return rejectWithValue(res?.error);
    }

    //Step2 :prepay check
    const checkRes = await dispatch(prePayCheck.initiate({ number: orderNumber }));
    if ('error' in checkRes) {
      return rejectWithValue(checkRes?.error);
    }
    // Step3: confirm pay
    const payRes = await dispatch(confirmPayments.initiate({ number: orderNumber }));
    if ('error' in payRes) {
      await dispatch(getOrderByOrderNumber.initiate(orderNumber));
      return rejectWithValue(payRes?.error);
    }
    // Step4: complete pay
    const completeRes = await dispatch(completePay.initiate({ number: orderNumber }));
    if ('error' in completeRes) {
      return rejectWithValue(completeRes?.error);
    }
    await dispatch(handlePurchase());
    return completeRes.data;
  }
);

export const removePayMethodCommand = createAsyncThunk(
  'removePayMethodCommand',
  async (payload: { number: string; id: number }, { dispatch, rejectWithValue }) => {
    const res = await dispatch(removePayMethod.initiate(payload));
    if ('error' in res) {
      return rejectWithValue(res?.error);
    }
    return res.data;
  }
);

export const addAddressForNewCustomerCommand = createAsyncThunk(
  'checkout/addAddressForNewCustomerCommand',
  async ({ address, uid }: { uid: number; address: any }, { dispatch, rejectWithValue }) => {
    try {
      const { data: newAddresses } = await dispatch(
        addAddressToUserByUid.initiate({
          uid: uid,
          address: address,
        })
      );
      const newAddress = newAddresses[newAddresses.length - 1];
      if (!newAddress?.id) {
        throw new Error('Address id not found');
      }
      await dispatch(
        getAddressesByUserId.initiate(uid, {
          forceRefetch: true,
        })
      );
      return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addAddressCommand = createAsyncThunk(
  'checkout/addAddressCommand',
  async (
    { address, uid, type }: { uid: number; address: any; type?: 'ship_address' | 'bill_address' },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const rootState = getState() as RootState;
      const { data: newAddresses } = await dispatch(
        addAddressToUserByUid.initiate({
          uid: uid,
          address: address,
        })
      );
      const newAddress = newAddresses[newAddresses.length - 1];
      if (!newAddress?.id) {
        throw new Error('Address id not found');
      }
      await dispatch(
        getAddressesByUserId.initiate(uid, {
          forceRefetch: true,
        })
      );
      const { billingAddress, shippingAddress } = selectCheckoutAddress(rootState);
      const orderNumber = selectCurrentOrderNumber(rootState);
      if (!orderNumber) {
        throw new Error('Order number not found');
      }

      if (!(billingAddress && shippingAddress)) {
        return await dispatch(
          changeAddressByOrderNumber.initiate({
            number: orderNumber,
            ship_address_attributes: newAddress,
            bill_address_attributes: newAddress,
          })
        ).unwrap();
      }

      if (type === 'bill_address') {
        await dispatch(
          changeAddressByOrderNumber.initiate({
            number: orderNumber,
            ship_address_attributes: shippingAddress,
            bill_address_attributes: newAddress,
          })
        ).unwrap();
      }

      if (type === 'ship_address') {
        await dispatch(
          changeAddressByOrderNumber.initiate({
            number: orderNumber,
            bill_address_attributes: billingAddress,
            ship_address_attributes: newAddress,
          })
        ).unwrap();
      }
      return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const changeDeliveryOptionCommand = createAsyncThunk(
  'checkout/changeDeliveryOptionCommand',
  async (mode: 'lead_time' | 'all_together', { dispatch, rejectWithValue, getState }) => {
    const rootState = getState() as RootState;
    const number = selectCurrentOrderNumber(rootState);
    if (!number) {
      return rejectWithValue('Order number not found');
    }
    const res = await dispatch(changeDeliveryOption.initiate({ number, mode })).unwrap();
    if ('error' in res) {
      return rejectWithValue(res?.error);
    }
    return;
  }
);
